import { db, guessAisle } from '../db/database';
import useAuthStore from '../store/useAuthStore';
import useDataStore from '../store/useDataStore';
import useAppStore from '../store/useAppStore';
import {
  queueRecipeCreate, queueRecipeUpdate, queueRecipeDelete, queueMealPlanUpdate, fullSync,
} from './syncService';

const isCloudReady = () => {
  const { isAuthenticated, isConfigured } = useAuthStore.getState();
  return isAuthenticated && isConfigured;
};

export async function addRecipe(recipe) {
  const now = new Date().toISOString();
  const newRecipe = { ...recipe, createdAt: now, updatedAt: now, cookCount: 0, rating: 0, lastCookedAt: null };
  const id = await db.recipes.add(newRecipe);
  if (isCloudReady()) queueRecipeCreate({ ...newRecipe, id });
  return id;
}

export async function updateRecipe(id, updates) {
  const withTimestamp = { ...updates, updatedAt: new Date().toISOString() };
  await db.recipes.update(id, withTimestamp);
  if (isCloudReady()) queueRecipeUpdate(id, withTimestamp);
}

export async function deleteRecipe(id) {
  await db.recipes.delete(id);
  if (isCloudReady()) queueRecipeDelete(id);
}

export async function toggleFavorite(id) {
  const recipe = await db.recipes.get(id);
  if (!recipe) return;
  const updates = { favorite: !recipe.favorite, updatedAt: new Date().toISOString() };
  await db.recipes.update(id, updates);
  if (isCloudReady()) queueRecipeUpdate(id, updates);
}

export async function markAsCooked(id) {
  const recipe = await db.recipes.get(id);
  if (!recipe) return;
  const updates = {
    cookCount: (recipe.cookCount || 0) + 1,
    lastCookedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  await db.recipes.update(id, updates);
  if (isCloudReady()) queueRecipeUpdate(id, updates);
}

export async function submitReview(recipeId, rating) {
  const recipe = await db.recipes.get(recipeId);
  if (!recipe) return;
  const currentTotal = (recipe.communityRating || 0) * (recipe.communityRatingCount || 0);
  const newCount = (recipe.communityRatingCount || 0) + 1;
  const newRating = (currentTotal + rating) / newCount;
  await db.recipes.update(recipeId, {
    communityRating: Math.round(newRating * 10) / 10,
    communityRatingCount: newCount,
  });
}

export function getMealPlan(weekStart) {
  return useDataStore.getState().mealPlans.find((p) => p.weekStart === weekStart);
}

export async function updateMealPlan(weekStart, days) {
  const existing = await db.mealPlans.where('weekStart').equals(weekStart).first();
  if (existing) {
    await db.mealPlans.update(existing.id, { days });
  } else {
    await db.mealPlans.add({ weekStart, days });
  }
  if (isCloudReady()) queueMealPlanUpdate(weekStart, days);
}

export function getActiveGroceryList() {
  return useDataStore.getState().groceryLists.find((l) => l.active);
}

export async function addToGroceryList(items, recipeId = null) {
  let activeList = getActiveGroceryList();
  if (!activeList) {
    const id = await db.groceryLists.add({
      name: 'Shopping List',
      createdAt: new Date().toISOString(),
      active: 1,
    });
    activeList = { id };
  }
  const newItems = items.map((item) => ({
    listId: activeList.id,
    item: item.item || item,
    qty: item.qty || '',
    unit: item.unit || '',
    aisle: item.aisle || guessAisle(item.item || item),
    checked: false,
    recipeId,
    recipeTitle: item.recipeTitle || null,
  }));
  await db.groceryItems.bulkAdd(newItems);
}

export async function toggleGroceryItem(id) {
  const item = await db.groceryItems.get(id);
  if (item) await db.groceryItems.update(id, { checked: !item.checked });
}

export async function removeGroceryItem(id) {
  await db.groceryItems.delete(id);
}

export async function clearCheckedItems() {
  const checked = useDataStore.getState().groceryItems.filter((i) => i.checked);
  await db.groceryItems.bulkDelete(checked.map((i) => i.id));
}

export async function clearGroceryList() {
  const activeList = getActiveGroceryList();
  if (activeList) await db.groceryItems.where('listId').equals(activeList.id).delete();
}

export async function addGroceryItemsFromRecipes(recipeIds) {
  for (const recipeId of recipeIds) {
    const recipe = await db.recipes.get(recipeId);
    if (recipe?.ingredients) {
      const items = recipe.ingredients
        .filter((ing) => ing.item && ing.item.trim())
        .map((ing) => ({ ...ing, recipeTitle: recipe.title }));
      await addToGroceryList(items, recipeId);
    }
  }
}

export async function toggleUnitSystem(system) {
  useAppStore.getState().setUnitSystem(system);
  await db.settings.put({ key: 'unitSystem', value: system });
}

let syncInFlight = false;
export async function triggerSync() {
  if (!isCloudReady() || syncInFlight) return;
  syncInFlight = true;
  useDataStore.getState().setSyncing(true);
  try {
    return await fullSync();
  } finally {
    useDataStore.getState().setSyncing(false);
    syncInFlight = false;
  }
}
